import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import type { EventsResponse, EventResponse, EventCategoriesResponse, GetEventsParams } from '../model/types'

/**
 * API hooks для сущности: Событие
 */

const EVENTS_QUERY_KEY = 'events'
const EVENT_QUERY_KEY = 'event'
const EVENT_CATEGORIES_KEY = 'event-categories'

/**
 * Получить список событий с фильтрацией
 * 
 * @param params - Параметры фильтрации и пагинации
 * @returns Query объект TanStack Query
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useEvents({ categoryId: '123', isUpcoming: true })
 * ```
 */
export function useEvents(params?: GetEventsParams) {
  return useQuery({
    queryKey: [EVENTS_QUERY_KEY, params],
    queryFn: async () => {
      const searchParams = new URLSearchParams()
      
      if (params?.page) {
        searchParams.set('page', params.page.toString())
      }
      if (params?.limit) {
        searchParams.set('limit', params.limit.toString())
      }
      if (params?.categoryId) {
        searchParams.set('categoryId', params.categoryId)
      }
      if (params?.isUpcoming !== undefined) {
        searchParams.set('isUpcoming', params.isUpcoming.toString())
      }
      if (params?.status) {
        searchParams.set('status', params.status)
      }
      if (params?.search) {
        searchParams.set('search', params.search)
      }

      const queryString = searchParams.toString()
      const endpoint = `/api/public/events${queryString ? `?${queryString}` : ''}`
      
      return apiClient<EventsResponse>(endpoint)
    },
    staleTime: 5 * 60 * 1000, // 5 минут
  })
}

/**
 * Получить детали события по ID
 * 
 * @param eventId - ID события
 * @returns Query объект TanStack Query
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useEventById('event-123')
 * ```
 */
export function useEventById(eventId: string) {
  return useQuery({
    queryKey: [EVENT_QUERY_KEY, eventId],
    queryFn: async () => {
      return apiClient<EventResponse>(`/api/public/events/${eventId}`)
    },
    enabled: !!eventId,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Получить категории событий
 */
export function useEventCategories() {
  return useQuery({
    queryKey: [EVENT_CATEGORIES_KEY],
    queryFn: () => apiClient<EventCategoriesResponse>('/api/public/events/categories'),
    staleTime: 10 * 60 * 1000, // 10 минут
  })
}

